import { useCallback, useEffect, useState } from 'react'
import { fechaCorta } from '../datos/formato.js'
import { Boton } from '../comunes/Boton.js'

/**
 * El odontograma del paciente (§4.5).
 *
 * **Lee y escribe por red, como la ficha extendida.** Lo clínico no entra en el
 * pull operativo: un odontograma por paciente en cada arranque haría inusable
 * la PWA en una conexión móvil. Se pide al abrir la atención del paciente que
 * se está atendiendo, y no antes.
 *
 * **Cada registro es una fila nueva, nunca un UPDATE.** El estado actual de una
 * pieza es su último registro; el historial completo queda. Por eso al tocar
 * una pieza no se «edita» nada: se agrega lo que se ve hoy.
 */

const API = '/api/clinico'

export type Hallazgo =
  | 'sano' | 'caries' | 'obturado' | 'ausente' | 'corona'
  | 'implante' | 'endodoncia' | 'fractura' | 'sellante' | 'a_extraer'
  | 'erupcionando'

interface Registro {
  id: string
  pieza: string
  cara: string | null
  caras: string[]
  hallazgo: Hallazgo
  estadoClinico: 'condicion' | 'existente' | 'planificado' | 'realizado'
  servicioId: string | null
  registradoAt: string
  nota: string | null
}

/**
 * Los hallazgos con su color, ordenados por gravedad.
 *
 * El color codifica estado clínico, no decora: verde lo sano, ámbar lo tratado,
 * rojo lo que necesita trabajo. Un odontograma se lee de un vistazo o no sirve.
 */
const HALLAZGOS: { id: Hallazgo; etiqueta: string; clase: string; punto: string }[] = [
  { id: 'sano',       etiqueta: 'Sano',        clase: 'bg-white text-slate-700 border-slate-300',      punto: 'bg-slate-200' },
  { id: 'caries',     etiqueta: 'Caries',      clase: 'bg-rose-50 text-rose-800 border-rose-300',      punto: 'bg-rose-500' },
  { id: 'obturado',   etiqueta: 'Obturado',    clase: 'bg-sky-50 text-sky-800 border-sky-300',         punto: 'bg-sky-500' },
  { id: 'endodoncia', etiqueta: 'Endodoncia',  clase: 'bg-violet-50 text-violet-800 border-violet-300', punto: 'bg-violet-500' },
  { id: 'corona',     etiqueta: 'Corona',      clase: 'bg-amber-50 text-amber-800 border-amber-300',   punto: 'bg-amber-500' },
  { id: 'implante',   etiqueta: 'Implante',    clase: 'bg-teal-50 text-teal-800 border-teal-300',      punto: 'bg-teal-500' },
  { id: 'sellante',   etiqueta: 'Sellante',    clase: 'bg-lime-50 text-lime-800 border-lime-300',      punto: 'bg-lime-500' },
  { id: 'erupcionando', etiqueta: 'Erupcionando', clase: 'bg-cyan-50 text-cyan-800 border-cyan-300', punto: 'bg-cyan-500' },
  { id: 'fractura',   etiqueta: 'Fractura',    clase: 'bg-orange-50 text-orange-900 border-orange-300', punto: 'bg-orange-500' },
  { id: 'a_extraer',  etiqueta: 'A extraer',   clase: 'bg-red-100 text-red-900 border-red-400',        punto: 'bg-red-600' },
  { id: 'ausente',    etiqueta: 'Ausente',     clase: 'bg-slate-200 text-slate-500 border-slate-300',  punto: 'bg-slate-400' },
]

const ESTILO = new Map(HALLAZGOS.map((h) => [h.id, h]))

const ESTADOS_CLINICOS = {
  condicion: {
    etiqueta: 'Condición',
    corto: 'C',
    clase: 'border-rose-500 bg-rose-600 text-white',
  },
  existente: {
    etiqueta: 'Existente',
    corto: 'E',
    clase: 'border-sky-500 bg-sky-600 text-white',
  },
  planificado: {
    etiqueta: 'Planificado',
    corto: 'P',
    clase: 'border-violet-500 bg-violet-600 text-white',
  },
  realizado: {
    etiqueta: 'Realizado hoy',
    corto: '✓',
    clase: 'border-emerald-500 bg-emerald-600 text-white',
  },
} satisfies Record<Registro['estadoClinico'], { etiqueta: string; corto: string; clase: string }>

const CARA_CORTA: Record<string, string> = {
  oclusal: 'O', incisal: 'I', mesial: 'M', distal: 'D', vestibular: 'V', lingual: 'L',
}

/**
 * Notación FDI. Cada cuadrante se lee desde la línea media hacia afuera, que es
 * como los escribe un odontólogo: arriba-derecha 18→11, arriba-izquierda 21→28.
 */
const SUPERIOR_DER = [18, 17, 16, 15, 14, 13, 12, 11]
const SUPERIOR_IZQ = [21, 22, 23, 24, 25, 26, 27, 28]
const INFERIOR_DER = [48, 47, 46, 45, 44, 43, 42, 41]
const INFERIOR_IZQ = [31, 32, 33, 34, 35, 36, 37, 38]
const TEMP_SUPERIOR_DER = [55, 54, 53, 52, 51]
const TEMP_SUPERIOR_IZQ = [61, 62, 63, 64, 65]
const TEMP_INFERIOR_DER = [85, 84, 83, 82, 81]
const TEMP_INFERIOR_IZQ = [71, 72, 73, 74, 75]

export function Odontograma({ clienteId, citaId, servicios = [], onSugerirCargo }: { clienteId: string; citaId: string | null; servicios?: { id: string; nombre: string; activo: boolean }[]; onSugerirCargo?: (sugerencia: { servicioId: string; piezaEstadoId: string }) => void }) {
  const [estado, setEstado] = useState<Map<string, Registro>>(new Map())
  const [cargando, setCargando] = useState(true)
  /** `null` mientras todo va bien; si no, por qué no se puede mostrar. */
  const [impedimento, setImpedimento] = useState<'sin_red' | 'sin_permiso' | null>(null)
  const [seleccionada, setSeleccionada] = useState<string | null>(null)
  const [denticion, setDenticion] = useState<'mixta' | 'permanente' | 'temporal'>('mixta')

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const res = await fetch(`${API}/${clienteId}/odontograma`, { credentials: 'include' })
      // Un 403 no es un problema de conexión: es que este rol no ve lo
      // clínico. Decir «no hay red» ahí manda a la recepcionista a revisar el
      // wifi por algo que nunca va a poder ver.
      if (res.status === 403) {
        setImpedimento('sin_permiso')
        return
      }
      if (!res.ok) throw new Error('no disponible')
      const cuerpo = (await res.json()) as { piezas: Registro[] }
      setEstado(new Map(cuerpo.piezas.map((p) => [p.pieza, p])))
      setImpedimento(null)
    } catch {
      // Sin red se atiende igual: el odontograma avisa y no bloquea nada.
      setImpedimento('sin_red')
    } finally {
      setCargando(false)
    }
  }, [clienteId])

  useEffect(() => {
    void cargar()
  }, [cargar])

  if (cargando) return <p className="txt-meta">Cargando el odontograma…</p>

  if (impedimento === 'sin_permiso') {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-5 text-center">
        <p className="text-sm text-slate-600">
          El odontograma lo ve y lo carga quien atiende.
        </p>
        <p className="txt-meta mt-1">
          Desde recepción se puede seguir con el cargo y el cobro.
        </p>
      </div>
    )
  }

  if (impedimento === 'sin_red') {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-5 text-center">
        <p className="text-sm text-slate-600">
          El odontograma necesita conexión y ahora no la hay.
        </p>
        <p className="txt-meta mt-1">
          La atención sigue: podés cargar el servicio y cobrar igual.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-3 rounded-xl border border-teal-100 bg-teal-50 px-3 py-2.5">
        <p className="text-xs font-semibold text-teal-900">Elegí una pieza para registrar el diagnóstico o tratamiento</p>
        <p className="mt-0.5 text-[11px] text-teal-700">Cada pieza muestra su estado clínico y las superficies afectadas directamente en la arcada.</p>
      </div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex rounded-lg bg-slate-100 p-1">
          {(['mixta', 'permanente', 'temporal'] as const).map((tipo) => <button key={tipo} type="button" onClick={() => setDenticion(tipo)} className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize ${denticion === tipo ? 'bg-white text-teal-800 shadow-sm' : 'text-slate-500'}`}>{tipo}</button>)}
        </div>
        <span className="text-xs text-slate-500">Vista desde la posición del odontólogo</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white px-4 py-5">
        <div className="mx-auto w-max space-y-1">
          <div className="flex justify-between text-[10px] uppercase tracking-wide text-slate-400"><span>Derecha del paciente</span><span>Izquierda del paciente</span></div>
          {denticion !== 'temporal' && <><p className="text-center text-[11px] uppercase tracking-wide text-slate-400">Dentición permanente</p>
          <Arcada izquierda={SUPERIOR_DER} derecha={SUPERIOR_IZQ} estado={estado}
                  seleccionada={seleccionada} onElegir={setSeleccionada} />
          <div className="h-px bg-slate-200" />
          <Arcada izquierda={INFERIOR_DER} derecha={INFERIOR_IZQ} estado={estado}
                  seleccionada={seleccionada} onElegir={setSeleccionada} /></>}
          {denticion === 'mixta' && <div className="h-3" />}
          {denticion !== 'permanente' && <><p className="pt-2 text-center text-[11px] uppercase tracking-wide text-slate-400">Dentición temporal</p>
          <Arcada izquierda={TEMP_SUPERIOR_DER} derecha={TEMP_SUPERIOR_IZQ} estado={estado}
                  seleccionada={seleccionada} onElegir={setSeleccionada} />
          <div className="h-px bg-slate-200" />
          <Arcada izquierda={TEMP_INFERIOR_DER} derecha={TEMP_INFERIOR_IZQ} estado={estado}
                  seleccionada={seleccionada} onElegir={setSeleccionada} /></>}
        </div>
      </div>

      {seleccionada ? (
        <Pieza
          clienteId={clienteId}
          citaId={citaId}
          pieza={seleccionada}
          actual={estado.get(seleccionada)}
          servicios={servicios}
          onCerrar={() => setSeleccionada(null)}
          onRegistrado={() => {
            setSeleccionada(null)
            void cargar()
          }}
          onSugerirCargo={onSugerirCargo}
        />
      ) : (
        <Referencias />
      )}
    </div>
  )
}

function Arcada({
  izquierda,
  derecha,
  estado,
  seleccionada,
  onElegir,
}: {
  izquierda: number[]
  derecha: number[]
  estado: Map<string, Registro>
  seleccionada: string | null
  onElegir: (p: string) => void
}) {
  return (
    <div className="flex gap-3">
      {[izquierda, derecha].map((cuadrante, i) => (
        <div key={i} className="flex gap-1">
          {cuadrante.map((n) => {
            const pieza = String(n)
            const registro = estado.get(pieza)
            const estilo = ESTILO.get(registro?.hallazgo ?? 'sano')!
            const estadoClinico = registro ? ESTADOS_CLINICOS[registro.estadoClinico ?? 'condicion'] : null
            const caras = registro?.caras ?? (registro?.cara ? [registro.cara] : [])
            const superficies = caras.map((cara) => CARA_CORTA[cara] ?? cara.slice(0, 1).toUpperCase()).join('')
            return (
              <button
                key={pieza}
                type="button"
                onClick={() => onElegir(pieza)}
                title={`Pieza ${pieza} · ${estilo.etiqueta}${estadoClinico ? ` · ${estadoClinico.etiqueta}` : ''}${superficies ? ` · Superficies ${superficies}` : ''}`}
                className={`relative h-14 w-10 rounded-lg border text-[11px] font-semibold tabular-nums shadow-sm transition hover:-translate-y-0.5 hover:shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${estilo.clase} ${
                  seleccionada === pieza ? 'ring-2 ring-teal-600 ring-offset-1' : ''
                }`}
              >
                {estadoClinico && (
                  <span className={`absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border px-1 text-[9px] font-bold shadow ${estadoClinico.clase}`}>
                    {estadoClinico.corto}
                  </span>
                )}
                <span className="block text-xs">{pieza}</span>
                <span className={`mt-1 block h-3 text-[9px] font-bold tracking-wide ${superficies ? 'text-current' : 'opacity-0'}`}>
                  {superficies || '—'}
                </span>
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

function Referencias() {
  return (
    <div className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Estado del trabajo</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {Object.entries(ESTADOS_CLINICOS).map(([id, estado]) => (
            <span key={id} className="flex items-center gap-1.5 text-[12px] text-slate-700">
              <span className={`flex h-5 min-w-5 items-center justify-center rounded-full border px-1 text-[9px] font-bold ${estado.clase}`}>{estado.corto}</span>
              {estado.etiqueta}
            </span>
          ))}
        </div>
      </div>
      <div className="border-t border-slate-200 pt-2.5">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Hallazgo</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {HALLAZGOS.filter((h) => h.id !== 'sano').map((h) => (
            <span key={h.id} className="flex items-center gap-1.5 text-[12px] text-slate-600">
              <span className={`h-2.5 w-2.5 rounded-full ${h.punto}`} />
              {h.etiqueta}
            </span>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-slate-500"><strong>Superficies:</strong> O oclusal · I incisal · M mesial · D distal · V vestibular · L lingual</p>
    </div>
  )
}

function Pieza({
  clienteId,
  citaId,
  pieza,
  actual,
  onCerrar,
  onRegistrado,
  servicios,
  onSugerirCargo,
}: {
  clienteId: string
  citaId: string | null
  pieza: string
  actual: Registro | undefined
  onCerrar: () => void
  onRegistrado: () => void
  servicios: { id: string; nombre: string; activo: boolean }[]
  onSugerirCargo?: ((sugerencia: { servicioId: string; piezaEstadoId: string }) => void) | undefined
}) {
  const [hallazgo, setHallazgo] = useState<Hallazgo>(actual?.hallazgo ?? 'caries')
  const [caras, setCaras] = useState<string[]>(actual?.caras ?? (actual?.cara ? [actual.cara] : []))
  const [estadoClinico, setEstadoClinico] = useState<Registro['estadoClinico']>(actual?.estadoClinico ?? 'condicion')
  const [servicioId, setServicioId] = useState(actual?.servicioId ?? '')
  const [nota, setNota] = useState('')
  const [historial, setHistorial] = useState<Registro[] | null>(null)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    void fetch(`${API}/${clienteId}/odontograma/historial?pieza=${pieza}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : { registros: [] }))
      .then((c: { registros: Registro[] }) => {
        if (vivo) setHistorial(c.registros)
      })
      .catch(() => {
        if (vivo) setHistorial([])
      })
    return () => {
      vivo = false
    }
  }, [clienteId, pieza])

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[15px] font-medium text-slate-900">
          Pieza {pieza}
          {actual && (
            <span className="ml-2 text-[13px] font-normal text-slate-500">
              hoy figura como {ESTILO.get(actual.hallazgo)!.etiqueta.toLowerCase()}
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={onCerrar}
          className="text-[13px] text-slate-500 underline decoration-dotted hover:text-slate-800"
        >
          Cerrar
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {HALLAZGOS.map((h) => (
          <button
            key={h.id}
            type="button"
            onClick={() => setHallazgo(h.id)}
            className={`rounded-full border px-3 py-1 text-[13px] transition ${h.clase} ${
              hallazgo === h.id ? 'ring-2 ring-teal-600 ring-offset-1' : 'opacity-70 hover:opacity-100'
            }`}
          >
            {h.etiqueta}
          </button>
        ))}
      </div>

      <div className="mt-4">
        <p className="txt-meta mb-1.5">Estado clínico</p>
        <div className="grid gap-2 sm:grid-cols-4">{([['condicion', 'Condición', 'Hallazgo observado'], ['existente', 'Existente', 'Hecho anteriormente'], ['planificado', 'Planificado', 'Pendiente de realizar'], ['realizado', 'Realizado hoy', 'Completado en esta consulta']] as const).map(([id, etiqueta, ayuda]) => <button key={id} type="button" onClick={() => setEstadoClinico(id)} className={`rounded-lg border p-2 text-left ${estadoClinico === id ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600' : 'border-slate-200 bg-white'}`}><span className="block text-xs font-semibold text-slate-800">{etiqueta}</span><span className="text-[10px] text-slate-500">{ayuda}</span></button>)}</div>
      </div>

      {['caries', 'obturado', 'fractura', 'sellante'].includes(hallazgo) && <div className="mt-3">
        <p className="txt-meta mb-1.5">Superficie dental</p>
        <div className="flex flex-wrap gap-2">{([['oclusal', 'Oclusal'], ['incisal', 'Incisal'], ['mesial', 'Mesial'], ['distal', 'Distal'], ['vestibular', 'Vestibular'], ['lingual', 'Lingual']] as const).map(([id, etiqueta]) => <button key={id} type="button" onClick={() => setCaras(caras.includes(id) ? caras.filter((cara) => cara !== id) : [...caras, id])} className={`rounded-lg border px-3 py-1.5 text-xs ${caras.includes(id) ? 'border-teal-600 bg-teal-50 font-medium text-teal-900' : 'border-slate-300 bg-white text-slate-600'}`}>{etiqueta}</button>)}</div>
      </div>}

      {['planificado', 'realizado'].includes(estadoClinico) && servicios.length > 0 && <label className="mt-3 block text-xs text-slate-500">Procedimiento relacionado<select value={servicioId} onChange={(e) => setServicioId(e.target.value)} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"><option value="">Sin vincular</option>{servicios.filter((s) => s.activo).map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}</select></label>}

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="min-w-0 flex-1">
          <span className="txt-meta block">Nota (opcional)</span>
          <input
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Cara oclusal, controlar en 6 meses…"
            className="mt-1 h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
          />
        </label>
        <Boton
          variante="primary"
          disabled={guardando}
          onClick={async () => {
            setGuardando(true)
            setError(null)
            try {
              const res = await fetch(`${API}/${clienteId}/odontograma`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  pieza,
                  hallazgo,
                  cara: null,
                  caras: ['caries', 'obturado', 'fractura', 'sellante'].includes(hallazgo) ? caras : [],
                  estadoClinico,
                  servicioId: servicioId || null,
                  citaId,
                  nota: nota.trim() || null,
                }),
              })
              if (!res.ok) {
                const cuerpo = (await res.json().catch(() => ({}))) as { mensaje?: string }
                setError(cuerpo.mensaje ?? 'No pudimos registrar el hallazgo')
                return
              }
              const creado = await res.json() as { id: string }
              if (estadoClinico === 'realizado' && servicioId) onSugerirCargo?.({ servicioId, piezaEstadoId: creado.id })
              onRegistrado()
            } finally {
              setGuardando(false)
            }
          }}
        >
          Registrar
        </Boton>
      </div>

      {error && <p className="mt-2 text-[13px] text-rose-700">{error}</p>}

      {/* El historial es la razón de ser del modelo: lo que se registra no se
          pisa, se apila. Sirve cuando alguien pregunta desde cuándo. */}
      {historial && historial.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-3">
          <p className="txt-meta mb-1.5">Historia de esta pieza</p>
          <ul className="space-y-1">
            {historial.map((r) => (
              <li key={r.id} className="flex items-baseline gap-2 text-[13px]">
                <span className="w-20 shrink-0 tabular-nums text-slate-400">
                  {fechaCorta(r.registradoAt)}
                </span>
                <span className="text-slate-700">{ESTILO.get(r.hallazgo)!.etiqueta}</span>
                {r.nota && <span className="truncate text-slate-500">· {r.nota}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
